import { useLayoutEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import storageManager from '@/api/storageManager';
import { saveCookieConsent } from '@/api/query/cookieConsent';
import { useTracking } from '@/common/contexts/TrackingContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import {
	COOKIE_CONSENT_ALL,
	COOKIE_CONSENT_DEFAULTS,
} from '../constants/cookieConsent.constants';

const getOrCreateBrowserId = () => {
	let browserId = storageManager.browsingPerson.getBrowsingPersonId();

	if (!browserId) {
		browserId = uuidv4();
		storageManager.browsingPerson.setBrowsingPersonId(browserId);
	}

	return browserId;
};

const useCookieConsent = () => {
	const t = useLanguageData('cookieConsent');
	const { setMarketingAllowed } = useTracking();
	const [hasAnswered, setHasAnswered] = useState(() =>
		storageManager.cookieConsent.hasAnswered(),
	);
	const [isCustomizing, setIsCustomizing] = useState(false);
	const [choices, setChoices] = useState(
		() => storageManager.cookieConsent.get() ?? { ...COOKIE_CONSENT_DEFAULTS },
	);

	const saveCookieConsentMutation = useMutation({
		mutationFn: saveCookieConsent,
	});

	useLayoutEffect(() => {
		if (!storageManager.cookieConsent.allowsMarketing()) {
			return;
		}

		setMarketingAllowed(true);
	}, []);

	const dismissWithChoices = (nextChoices) => {
		storageManager.cookieConsent.save(nextChoices);
		setChoices(nextChoices);
		setHasAnswered(true);
		setMarketingAllowed(nextChoices.marketing);
		saveCookieConsentMutation.mutate({
			browserId: getOrCreateBrowserId(),
			...nextChoices,
		});
	};

	const handleAcceptAll = () => {
		dismissWithChoices({ ...COOKIE_CONSENT_ALL });
	};

	const handleSaveSelected = () => {
		dismissWithChoices(choices);
	};

	const handleRejectAll = () => {
		dismissWithChoices({ ...COOKIE_CONSENT_DEFAULTS });
	};

	const handleCustomize = () => {
		setIsCustomizing(true);
	};

	const toggleChoice = (key) => {
		setChoices((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	return {
		t,
		hasAnswered,
		isCustomizing,
		choices,
		toggleChoice,
		handleAcceptAll,
		handleSaveSelected,
		handleRejectAll,
		handleCustomize,
	};
};

export default useCookieConsent;
